<template>
  <div class="app-tariff-status-wrapper">
    <ContractSummary>
      <template v-for="slot in possibleChildSlotsFor('ContractSummary')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ContractSummary>
    <div v-if="view === 'deactivate'">
      <Deactivate @refresh="refresh" :item="toDeactivateItem">
        <template v-for="slot in possibleChildSlotsFor('Deactivate')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Deactivate>
    </div>
    <div v-if="view === 'overview' && forceUpdate">
    <slot name="txtBefore"></slot>
    <div v-if="hasTariffsOrOptions" class="tariff-status">
      <Item class="pending-tariff" v-if="fullPendingTariff" :item="fullPendingTariff" @selected="selectedItem" item-type="pendingTariff">
        <template v-for="slot in possibleChildSlotsFor('PendingTariff')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Item>
      <Item class="active-tariff" v-if="fullBookedTariff" :item="fullBookedTariff" :has-pending="hasPending" @selected="selectedItem"
            item-type="bookedTariff">
        <template v-for="slot in possibleChildSlotsFor('BookedTariff')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Item>
      <Item class="tariff-options" v-for="option in fullBookedTariffOptions" :key="option.tariffOptionId" @selected="selectedItem" :item="option"
            item-type="tariffOption">
        <template v-for="slot in possibleChildSlotsFor('TariffOptions')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Item>
      <Item class="service-options" v-if="bookedServiceOptions" @selected="selectedItem" :item="bookedServiceOptions"
            item-type="serviceOption" :show-consumptions="false">
        <template v-for="slot in possibleChildSlotsFor('ServiceOptions')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </Item>
    </div>
    <div v-else class="no-tariff-status">
      <slot name="txtNoTariffsOrOptions"></slot>
    </div>
  </div>
  </div>
</template>

<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import slotProxy from '@/mixins/slotProxy'
import extraData from '@/mixins/extraData'
import transaction from '@/mixins/transaction'
import Item from './Item.vue'
import { mapGetters, mapMutations } from 'vuex'
import Deactivate from './deactivate/Deactivate.vue'
import ContractSummary from '@/components/process/generic/Tariff/generic/ContractSummary.vue'

const basicTariffId = '92'

export default {
  name: 'TariffStatusWrapper',
  components: { Deactivate, Item, ContractSummary },
  data () {
    return {
      tariffs: {},
      tariffoptions: {},
      serviceOptions: {},
      consumptions: {},
      selected_: {},
      loading: false
    }
  },
  methods: {
    ...mapMutations('tariffs', [
      'changeView'
    ]),
    setStep (view) {
      this.changeView({ view: view })
    },
    selectedItem (item) {
      this.selected_ = item
    },
    refresh () {
      this.$apollo.queries.tariffs.refresh()
    }
  },
  computed: {
    toDeactivateItem: function () {
      return this.selected_
    },
    ...mapGetters('tariffs', [
      'getView',
      'getForceUpdate'
    ]),

    forceUpdate () {
      if (this.getForceUpdate) {
        this.refresh()
        this.$store.commit('tariffs/forceUpdate', false)
      }
      return true
    },
    view: function () {
      return this.getView
    },
    fullBookedTariff: function () {
      const tariff = this.tariffs?.bookedTariff
      if (tariff) {
        if (tariff.tariffId !== basicTariffId) {
          const extra = this.extraData.find(elem => elem.tariffId === tariff.tariffId)
          const consumption = this.consumptions?.consumptionsForTariffAndOptions?.find(elem => {
            return (tariff.tariffId === elem.id)
          })
          return { ...extra, ...tariff, ...consumption }
        }
      }
      return null
    },
    fullPendingTariff: function () {
      const tariff = this.tariffs?.pendingTariff
      if (tariff) {
        if (tariff.tariffId !== basicTariffId) {
          const extra = this.extraData.find(elem => elem.tariffId === tariff.tariffId)
          return { ...extra, ...tariff }
        }
      }
      return null
    },
    fullBookedTariffOptions: function () {
      const tariffOptions = this.tariffoptions?.bookedTariffoptions?.bookedTariffoptions || []
      return tariffOptions.map((option) => {
        const extra = this.extraData.find(elem => elem.tariffOptionId === option.tariffoptionId)
        const consumption = this.consumptions?.consumptionsForTariffAndOptions?.find(elem => {
          return (option.tariffoptionId === elem.id)
        })
        return { ...extra, ...option, ...consumption }
      })
    },
    bookedServiceOptions: function () {
      if (this.serviceOptions?.anonymousCallRejection) {
        return this.extraData.find(elem => elem.serviceOptionId === 'acr')
      }
      return null
    },
    hasPending: function () {
      return this.fullPendingTariff !== null
    },
    hasTariffsOrOptions: function () {
      return (this.fullBookedTariff || this.fullPendingTariff || this.fullBookedTariffOptions.length !== 0)
    }
  },
  apollo: {
    tariffs: function () {
      return wrapQuery(
        gql`query bookedTariff {
         tariffs {
           bookedTariff {
             tariffId
             name
             basicFee
             formattedBasicFee
             statusKey
             smsFlat
             cancelable
             tariffChangePossible
             isPendingTariff
             isSuspendedActive
             phoneFlat
             tariffState
             tariffStateFlag
             renewContractDate
             possibleChangingDate
             terminationDate
             changeTariffDate
             runtime {
               amount
               unit
               formattedUnit
             }
           }
           pendingTariff {
             tariffId
             name
             tariffState
             basicFee
             formattedBasicFee
             tariffChangePossible
             isPendingTariff
             isSuspendedActive
             cancelable
             statusKey
             smsFlat
             phoneFlat
             phoneFlat
             tariffState
             renewContractDate
             possibleChangingDate
             terminationDate
             changeTariffDate
             runtime {
               amount
               unit
               formattedUnit
             }
           }
         }
         tariffoptions {
            bookedTariffoptions {
              bookedTariffoptions {
                automaticExtension
                tariffoptionId
                name
                price
                duration {
                  amount
                  unit
                  formattedUnit
                }
                statusKey
                state
                startOfRuntime
                endOfRuntime
                possibleChangingDate
                buttonText
                stateFlag
                cancelable
                formattedPrice
                restrictedService
                tariffState
              }
            }
           }
         consumptions {
            consumptionsForTariffAndOptions {
            consumptions {
              consumed,
              expirationDate,
              left,
              max,
              tariffOrOptions {
                id,
                isFlat,
                name,
                type,
              }
              type,
              unit
            }
            id,
            isFlat,
            name,
            type
            }
           }
           serviceOptions {
             anonymousCallRejection
           }
        }`, this, ['tariffs', 'tariffoptions', 'consumptions', 'serviceOptions'], {}, function (data) {
        })
    }
  },
  mixins: [
    slotProxy(),
    extraData(),
    transaction('tariffStatus')
  ]
}
</script>
